import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccessService } from 'app/core/services/access.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, forkJoin, map, of } from 'rxjs';

@Component({
  selector: 'dialog-add-department',
  templateUrl: './dialog-add-department.component.html',
  styleUrls: ['./dialog-add-department.component.scss']
})
export class DialogAddDepartmentComponent implements OnInit {

  isLoading: boolean = false;
  departmentGroup: FormGroup;
  department: any[] = [];
  filteredDepartments: any[] = [];
  selectedDepartments: any[] = [];
  departmentSearchText: string = '';

  branch: any[] = [];
  filteredBranches: any[] = [];
  selectedBranches: any[] = [];
  branchSearchText: string = '';

  clients: any[] = [];
  filteredClients: any[] = [];
  selectedClients: any[] = [];
  clientSearchText: string = '';

  constructor(public dialogRef: MatDialogRef<DialogAddDepartmentComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder, private accessService: AccessService, private toastr: ToastrService) {
    this.departmentGroup = this.fb.group({
      id: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.getDepartmentList();
    this.getBranchList();
    this.getClientList();
  }

  getDepartmentList() {
    this.accessService.getDepartmentList(this.data.employeeId).subscribe({
      next: res => {
        this.department = res;
        this.filteredDepartments = [...this._remainingDepartments()];
      },
      error: err => {
        console.log(err);
      }
    })
  }

  getBranchList() {
    this.accessService.getBranchList(this.data.employeeId).subscribe({
      next: res => {
        this.branch = res;
        this.filteredBranches = [...this._remainingBranches()];
      },
      error: err => {
        console.log(err);
      }
    })
  }

  getClientList() {
    this.accessService.getClientList(this.data.employeeId).subscribe({
      next: res => {
        this.clients = res;
        this.filteredClients = [...this._remainingClients()];
      },
      error: err => {
        console.log(err);
      }
    })
  }

  private _remainingDepartments(): any[] {
    return this.department.filter(
      dept => !this.selectedDepartments.some(selected => selected.id === dept.id)
    );
  }

  private _remainingBranches(): any[] {
    return this.branch.filter(
      branch => !this.selectedBranches.some(selected => selected.id === branch.id)
    );
  }

  private _remainingClients(): any[] {
    return this.clients.filter(
      client => !this.selectedClients.some(selected => selected.id === client.id)
    );
  }

  filterDepartments() {
    const searchText = this.departmentSearchText.toLowerCase();
    const remaining = this._remainingDepartments();
    this.filteredDepartments = remaining.filter(dept =>
      dept.name.toLowerCase().includes(searchText)
    );
  }

  filterBranches() {
    const searchText = this.branchSearchText.toLowerCase();
    const remaining = this._remainingBranches();
    this.filteredBranches = remaining.filter(branch =>
      branch.name.toLowerCase().includes(searchText)
    );
  }

  filterClients() {
    const searchText = this.clientSearchText.toLowerCase();
    const remaining = this._remainingClients();
    this.filteredClients = remaining.filter(client =>
      client.name.toLowerCase().includes(searchText)
    );
  }

  onDepartmentChange(event: any) {
    const deptId = event.value;
    const dept = this.department.find(d => d.id === deptId);
    if (dept && !this.selectedDepartments.find(d => d.id === dept.id)) {
      this.selectedDepartments.push(dept);
      this.departmentSearchText = '';
      this.filteredDepartments = [...this._remainingDepartments()];
    }
  }

  onBranchChange(event: any) {
    const branchId = event.value;
    const branch = this.branch.find(b => b.id === branchId);
    if (branch && !this.selectedBranches.find(b => b.id === branch.id)) {
      this.selectedBranches.push(branch);
      this.branchSearchText = '';
      this.filteredBranches = [...this._remainingBranches()];
    }
  }

  onClientChange(event: any) {
    const clientId = event.value;
    const client = this.clients.find(c => c.id === clientId);
    if (client && !this.selectedClients.find(c => c.id === client.id)) {
      this.selectedClients.push(client);
      this.clientSearchText = '';
      this.filteredClients = [...this._remainingClients()];
    }
  }

  onDropdownOpenChange(event: boolean, type: string) {
    if (event) {
      if (type === 'department') {
        this.departmentSearchText = '';
        this.filteredDepartments = [...this._remainingDepartments()];
      } else if (type === 'branch') {
        this.branchSearchText = '';
        this.filteredBranches = [...this._remainingBranches()];
      } else if (type === 'client') {
        this.clientSearchText = '';
        this.filteredClients = [...this._remainingClients()];
      }
    }
  }

  removeDepartment(dept: any): void {
    const index = this.selectedDepartments.indexOf(dept);
    if (index >= 0) {
      this.selectedDepartments.splice(index, 1);
      this.filteredDepartments = [...this._remainingDepartments()];
    }
  }

  removeBranch(branch: any): void {
    const index = this.selectedBranches.indexOf(branch);
    if (index >= 0) {
      this.selectedBranches.splice(index, 1);
      this.filteredBranches = [...this._remainingBranches()];
    }
  }

  removeClient(client: any): void {
    const index = this.selectedClients.indexOf(client);
    if (index >= 0) {
      this.selectedClients.splice(index, 1);
      this.filteredClients = [...this._remainingClients()];
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  submit() {
    this.isLoading = true;

    const requests = this.selectedDepartments.map(dept => {
      return this.accessService.insertDepartmentAdmin(dept.id, this.data.employeeId).pipe(
        map(response => ({ response, departmentName: dept.name })),
        catchError(error => of({ response: { message: 'error' }, departmentName: dept.name }))
      );
    });

    forkJoin(requests).subscribe({
      next: results => {
        this.isLoading = false;

        const duplicates = results.filter(r => r.response.message === 'Department already exists');
        const successes = results.filter(r => r.response.message !== 'Department already exists' && r.response.message !== 'error');

        if (duplicates.length > 0) {
          const duplicateNames = duplicates.map(d => d.departmentName).join(', ');
          this.showNotificationError(`${duplicateNames} already exists.`);
        }

        if (successes.length > 0) {
          this.dialogRef.close(results);
        }
      },
      error: err => {
        console.log(err);
        this.isLoading = false;
      }
    });
  }

  submitBranch() {
    this.isLoading = true;

    const requests = this.selectedBranches.map(branch => {
      return this.accessService.insertBranchAdmin(branch.id, this.data.employeeId).pipe(
        map(response => ({ response, branchName: branch.name })),
        catchError(error => of({ response: { message: 'error' }, branchName: branch.name }))
      );
    });

    forkJoin(requests).subscribe({
      next: results => {
        this.isLoading = false;

        const duplicates = results.filter(r => r.response.message === 'Branch already exists');
        const successes = results.filter(r => r.response.message !== 'Branch already exists' && r.response.message !== 'error');

        if (duplicates.length > 0) {
          const duplicateNames = duplicates.map(d => d.branchName).join(', ');
          this.showNotificationError(`${duplicateNames} already exists.`);
        }

        if (successes.length > 0) {
          this.dialogRef.close(results);
        }
      },
      error: err => {
        console.log(err);
        this.isLoading = false;
      }
    });
  }

  submitClient() {
    this.isLoading = true;

    const requests = this.selectedClients.map(client => {
      return this.accessService.insertClient(client.id, this.data.employeeId).pipe(
        map(response => ({ response, clientName: client.name })),
        catchError(error => of({ response: { message: 'error' }, clientName: client.name }))
      );
    });

    forkJoin(requests).subscribe({
      next: results => {
        this.isLoading = false;

        const duplicates = results.filter(r => r.response.message === 'Client already exists');
        const successes = results.filter(r => r.response.message !== 'Client already exists' && r.response.message !== 'error');

        if (duplicates.length > 0) {
          const duplicateNames = duplicates.map(d => d.clientName).join(', ');
          this.showNotificationError(`${duplicateNames} already exists.`);
        }

        if (successes.length > 0) {
          this.dialogRef.close(results);
        }
      },
      error: err => {
        console.log(err);
        this.isLoading = false;
      }
    });
  }

  showNotificationError(text) {
    this.toastr.success('<span class="now-ui-icons ui-1_bell-53"></span> ' + text + '', '', {
      timeOut: 3000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-danger alert-with-icon",
      positionClass: 'toast-' + 'top' + '-' + 'center'
    });
  }
}